import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import InputText from "components/_refactor/text/input";
import {
  generateFormularioDevolucaoReport,
  generateRelatorioFinanceiroReport,
} from "../utils/generateReports";

function unwrapResult(response) {
  if (response?.Success === false) {
    throw new Error(
      response?.ErrorMessage ||
        response?.ValidationMessage ||
        "Erro na requisição.",
    );
  }
  return response?.Result ?? response?.result ?? response;
}

export default function EditSolicitacaoDialog({
  open,
  onClose,
  solicitacao,
  onUpdated,
}) {
  const snackbar = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detail, setDetail] = useState(null);

  const [numeroProtocolo, setNumeroProtocolo] = useState("");
  const [numeroNotaDevolucao, setNumeroNotaDevolucao] = useState("");
  const [numeroNotaCredito, setNumeroNotaCredito] = useState("");

  const hasProtocolo = Boolean(detail?.NumeroProtocolo);
  const canEditNotas = hasProtocolo;
  const notasInalteradas =
    canEditNotas &&
    numeroNotaDevolucao.trim() === (detail?.NumeroNotaDevolucao || "") &&
    numeroNotaCredito.trim() === (detail?.NumeroNotaCredito || "");

  useEffect(() => {
    if (!open || !solicitacao?.IdSolicitacaoDevolucaoInterna) return;

    async function loadDetail() {
      setLoadingDetail(true);
      try {
        const res = await Api.get(
          "detail",
          "internalReturn",
          `?id=${solicitacao.IdSolicitacaoDevolucaoInterna}`,
          true,
        );
        const data = unwrapResult(res);
        setDetail(data);
        setNumeroProtocolo(data?.NumeroProtocolo || "");
        setNumeroNotaDevolucao(data?.NumeroNotaDevolucao || "");
        setNumeroNotaCredito(data?.NumeroNotaCredito || "");
      } catch (error) {
        snackbar.showMessage(
          error?.message ||
            "Não foi possível carregar os detalhes da solicitação.",
          { severity: "error" },
        );
      } finally {
        setLoadingDetail(false);
      }
    }

    loadDetail();
  }, [open, solicitacao]);

  const handleSubmit = async () => {
    if (!detail) return;

    const body = {
      IdSolicitacaoDevolucaoInterna: detail.IdSolicitacaoDevolucaoInterna,
    };

    if (!hasProtocolo) {
      if (!numeroProtocolo.trim()) {
        snackbar.showMessage("Informe o número do protocolo.", {
          severity: "warning",
        });
        return;
      }
      body.NumeroProtocolo = numeroProtocolo.trim();
    } else {
      if (numeroNotaDevolucao.trim())
        body.NumeroNotaDevolucao = numeroNotaDevolucao.trim();
      if (numeroNotaCredito.trim())
        body.NumeroNotaCredito = numeroNotaCredito.trim();

      if (!body.NumeroNotaDevolucao && !body.NumeroNotaCredito) {
        snackbar.showMessage(
          "Informe ao menos a nota de devolução ou a nota de crédito.",
          {
            severity: "warning",
          },
        );
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await Api.put("update", "internalReturn", body, true);
      unwrapResult(res);
      snackbar.showMessage("Solicitação atualizada com sucesso!", {
        severity: "success",
      });
      onUpdated();
      onClose();
    } catch (error) {
      snackbar.showMessage(
        error?.message || "Não foi possível atualizar a solicitação.",
        { severity: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateFormulario = () => {
    if (!detail?.NumeroProtocolo) {
      snackbar.showMessage("Informe o protocolo antes de gerar o relatório.", {
        severity: "warning",
      });
      return;
    }
    generateFormularioDevolucaoReport(detail);
  };

  const handleGenerateFinanceiro = () => {
    if (!detail?.NumeroNotaDevolucao || !detail?.NumeroNotaCredito) {
      snackbar.showMessage(
        "Informe a nota de devolução e a nota de crédito antes de gerar o relatório financeiro.",
        { severity: "warning" },
      );
      return;
    }
    generateRelatorioFinanceiroReport(detail);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="body1">
          Editar Solicitação de Devolução Interna
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loadingDetail ? (
          <Typography>Carregando...</Typography>
        ) : (
          <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            <Typography variant="body2">
              <strong>NF:</strong> {detail?.NumeroNota} |{" "}
              <strong>Cliente:</strong> {detail?.RazaoSocial}
            </Typography>

            {!canEditNotas && (
              <InputText
                label="Número do Protocolo"
                value={numeroProtocolo}
                onChange={(e) =>
                  setNumeroProtocolo(e.target.value.slice(0, 50))
                }
                required
              />
            )}

            {canEditNotas && (
              <>
                <Typography variant="body2">
                  <strong>Protocolo:</strong> {detail.NumeroProtocolo}
                </Typography>
                <InputText
                  label="Número da Nota de Devolução"
                  value={numeroNotaDevolucao}
                  onChange={(e) =>
                    setNumeroNotaDevolucao(e.target.value.slice(0, 50))
                  }
                />
                <InputText
                  label="Número da Nota de Crédito"
                  value={numeroNotaCredito}
                  onChange={(e) =>
                    setNumeroNotaCredito(e.target.value.slice(0, 50))
                  }
                />
                {detail?.TeraNotaCredito && (
                  <Typography variant="body2" color="primary">
                    Esta solicitação deve gerar Nota de Crédito.
                  </Typography>
                )}
              </>
            )}

            {hasProtocolo && (
              <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleGenerateFormulario}
                >
                  Gerar Formulário de Devolução
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleGenerateFinanceiro}
                  disabled={
                    !detail?.NumeroNotaDevolucao || !detail?.NumeroNotaCredito
                  }
                >
                  Gerar Relatório Financeiro (CRC)
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={submitting || loadingDetail || notasInalteradas}
        >
          {submitting ? "Salvando..." : "Confirmar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
